import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, ArrowRight, RefreshCw, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { auth } from '../services/api';
import { useAuth } from '../context/AuthContext';
import styles from './StudentOTPLogin.module.css';
import CanaryOperatorSignIn from './CanaryOperatorSignIn';

/**
 * Student OTP Login Page
 * Two-step verification: 1. Enter Student ID, 2. Enter OTP from email
 */
export default function StudentOTPLogin() {
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const [searchParams] = useSearchParams();
  const [canarySignIn, setCanarySignIn] = useState(false);
  
  // URL params
  const initialStudentId = searchParams.get('studentId') || '';
  const labId = searchParams.get('labId') || '';
  const requestedPath = searchParams.get('redirect');
  const redirectPath = requestedPath && /^\/(?![/\\])/.test(requestedPath) && !/[\\\u0000-\u001f\u007f]/.test(requestedPath)
    ? requestedPath : labId ? `/launch/${encodeURIComponent(labId)}` : '/';
  
  // State
  const [step, setStep] = useState(initialStudentId ? 'otp' : 'studentId'); // 'studentId' or 'otp'
  const [studentId, setStudentId] = useState(initialStudentId);
  const [otpCode, setOtpCode] = useState(['', '', '', '', '', '']);
  const [maskedEmail, setMaskedEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [devOtp, setDevOtp] = useState(''); // For development mode
  
  const otpInputRefs = useRef([]);
  
  // Auto-request OTP if studentId is provided in URL
  useEffect(() => {
    if (initialStudentId && step === 'otp') {
      handleRequestOTP(initialStudentId);
    }
  }, []);
  
  // Countdown timer for resend
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);
  
  // Handle student ID submission
  const handleStudentIdSubmit = async (e) => {
    e.preventDefault();
    if (!studentId.trim()) {
      setError('Please enter your student ID');
      return;
    }
    await handleRequestOTP(studentId);
  };
  
  // Request OTP
  const handleRequestOTP = async (id) => {
    setLoading(true);
    setError('');
    setDevOtp('');
    
    try {
      const response = await auth.requestOTP(id, labId);
      setMaskedEmail(response.email || `${id.slice(0, 2)}***@udst.edu.qa`);
      setStep('otp');
      setCountdown(60); // 60 second cooldown
      setSuccess('Verification code sent to your email');
      
      // In development mode, show the OTP
      if (response._devOtp) {
        setDevOtp(response._devOtp);
      }
      
      // Focus first OTP input
      setTimeout(() => {
        otpInputRefs.current[0]?.focus();
      }, 100);
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to send verification code';
      setError(message);
      
      // Handle rate limiting
      if (err.response?.data?.retryAfter) {
        setCountdown(err.response.data.retryAfter);
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Handle OTP input change
  const handleOtpChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;
    
    const newOtp = [...otpCode];
    newOtp[index] = value;
    setOtpCode(newOtp);
    setError('');
    
    // Auto-focus next input
    if (value && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }
    
    // Auto-submit when complete
    if (newOtp.every(d => d) && newOtp.join('').length === 6) {
      handleVerifyOTP(newOtp.join(''));
    }
  };
  
  // Handle OTP paste
  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData.length === 6) {
      const newOtp = pastedData.split('');
      setOtpCode(newOtp);
      handleVerifyOTP(pastedData);
    }
  };
  
  // Handle backspace
  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };
  
  // Verify OTP
  const handleVerifyOTP = async (code) => {
    setLoading(true);
    setError('');
    
    try {
      const response = await auth.verifyOTP(studentId, code);
      
      loginWithToken(response.token, response.user);
      navigate(redirectPath, { replace: true });
    } catch (err) {
      const data = err.response?.data || {};
      setError(data.message || 'Invalid verification code');
      setOtpCode(['', '', '', '', '', '']);
      otpInputRefs.current[0]?.focus();
      
      // If max attempts reached, go back to request new code
      if (data.reason === 'max_attempts' || data.reason === 'expired') {
        setTimeout(() => {
          setStep('studentId');
        }, 2000);
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Resend OTP
  const handleResend = () => {
    if (countdown > 0) return;
    setOtpCode(['', '', '', '', '', '']);
    handleRequestOTP(studentId);
  };
  
  if (canarySignIn) return <CanaryOperatorSignIn onBack={() => setCanarySignIn(false)} />;
  return (
    <div className={styles.container}>
      <div className={styles.card} role="main" aria-label="Student login">
        {/* Logo */}
        <div className={styles.logoSection}>
          <div className={styles.logoIcon} aria-hidden="true">
            <Mail size={28} />
          </div>
          <h1>Lab Environment</h1>
          <p>Student Verification</p>
        </div>
        
        {/* Success/Error Messages */}
        {error && (
          <div className={styles.alert + ' ' + styles.alertError} role="alert">
            <AlertCircle size={18} aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}

        {success && !error && (
          <div className={styles.alert + ' ' + styles.alertSuccess} role="status">
            <CheckCircle size={18} aria-hidden="true" />
            <span>{success}</span>
          </div>
        )}
        
        {/* Development OTP Display */}
        {devOtp && (
          <div className={styles.devOtp}>
            <span>Development Mode - OTP:</span>
            <code>{devOtp}</code>
          </div>
        )}
        
        {/* Step 1: Student ID */}
        {step === 'studentId' && (
          <form onSubmit={handleStudentIdSubmit} className={styles.form}>
            <div className={styles.formGroup}>
              <label htmlFor="studentId">Student ID</label>
              <input
                id="studentId"
                type="text"
                value={studentId}
                onChange={(e) => setStudentId(e.target.value)}
                placeholder="Enter your student ID (e.g., 60123456)"
                autoComplete="username"
                autoFocus
                disabled={loading}
              />
              <p className={styles.hint}>
                A verification code will be sent to your student email
              </p>
            </div>
            
            <button 
              type="submit" 
              className={styles.submitButton}
              disabled={loading || !studentId.trim()}
            >
              {loading ? (
                <>
                  <RefreshCw size={18} className={styles.spinning} />
                  Sending...
                </>
              ) : (
                <>
                  Send Verification Code
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>
        )}
        
        {/* Step 2: OTP Verification */}
        {step === 'otp' && (
          <div className={styles.form}>
            <div className={styles.otpInfo}>
              <p>Enter the 6-digit code sent to</p>
              <p className={styles.email}>{maskedEmail}</p>
            </div>
            
            <div className={styles.otpInputs} onPaste={handleOtpPaste} role="group" aria-label="Verification code">
              {otpCode.map((digit, index) => (
                <input
                  key={index}
                  ref={el => otpInputRefs.current[index] = el}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(index, e)}
                  disabled={loading}
                  className={styles.otpInput}
                  autoFocus={index === 0}
                  aria-label={`Digit ${index + 1} of 6`}
                />
              ))}
            </div>
            
            <div className={styles.resendSection}>
              {countdown > 0 ? (
                <p className={styles.countdown}>
                  <Clock size={14} />
                  Resend code in {countdown}s
                </p>
              ) : (
                <button 
                  type="button"
                  onClick={handleResend}
                  className={styles.resendButton}
                  disabled={loading}
                >
                  <RefreshCw size={14} />
                  Resend Code
                </button>
              )}
            </div>
            
            <button
              type="button"
              onClick={() => {
                setStep('studentId');
                setOtpCode(['', '', '', '', '', '']);
                setError('');
                setSuccess('');
              }}
              className={styles.backButton}
            >
              Use a different student ID
            </button>
          </div>
        )}
        
        {/* Lab info */}
        {!initialStudentId && !labId && <p>
          <button type="button" className={styles.backButton} onClick={() => setCanarySignIn(true)} disabled={loading}>
            Scoped operator sign-in
          </button>
        </p>}
        {labId && (
          <div className={styles.labInfo}>
            <p>Accessing lab environment</p>
          </div>
        )}
      </div>
    </div>
  );
}
